import { useEffect, useState } from 'react';
import { X, Folder, ChevronRight, Home } from '../../icons';
import { Button } from '../../components/shared';
import { listFolderChildren, type NodePublic } from '../../api/workspace';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 대상 폴더 선택 (2026-08-14) — "다음으로 이동" · "다음으로 복사" 공용.
 *
 * 두 동작은 **목적지를 고르는 방식이 같다**. 따로 만들면 한쪽만 고쳐지는 일이 반드시 생기므로
 * 하나로 두고 제목·버튼 문구만 바꾼다.
 *
 * 트리를 펼치는 대신 **한 겹씩 들어가는** 방식을 쓴다. 트리는 화면이 좁은 모달에서 들여쓰기가
 * 금방 바닥나고, 목적지를 고르는 데 필요한 것은 "지금 어디에 있나" 뿐이다.
 */

/** 경로에서 끝의 몇 단계를 그대로 보여 줄 것인가 — 나머지 가운데는 `…` 로 접는다. */
const CRUMB_TAIL = 2;

interface Props {
  /** 옮기거나 복사할 대상들 — 자기 자신 안으로는 들어갈 수 없다. */
  moving: readonly NodePublic[];
  mode: 'move' | 'copy';
  onClose: () => void;
  onPick: (parentId: string | null) => void | Promise<void>;
}

export function WorkspaceFolderPicker({ moving, mode, onClose, onPick }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [path, setPath] = useState<NodePublic[]>([]);
  const [folders, setFolders] = useState<NodePublic[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeClose(onClose, busy);

  const currentId = path.length > 0 ? path[path.length - 1]!.id : null;
  const movingIds = new Set(moving.map((m) => m.id));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await listFolderChildren(currentId ?? 'root');
        if (cancelled) return;
        // 폴더만 — 목적지는 폴더뿐이다.
        setFolders(res.children.filter((c) => c.type === 'folder'));
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentId]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onPick(currentId);
    } catch (err) {
      setError((err as any)?.response?.data?.message ?? (err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div
      className="workspace-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tWorkspace(mode === 'move' ? 'workspace.row.move' : 'workspace.row.copy_to')}
    >
      <div ref={trapRef} className="workspace-modal ws-picker-modal">
        <header className="workspace-modal-header">
          <h2>{tWorkspace(mode === 'move' ? 'workspace.picker.move_heading' : 'workspace.picker.copy_heading')}</h2>
          <Button variant="ghost" size="sm" iconOnly
            onClick={onClose} disabled={busy} aria-label={tWorkspace('workspace.modal.aria_close')}
            iconLeft={<X size={18} />}
          />
        </header>

        <div className="workspace-modal-body">
          <div className="workspace-modal-context">
            {tWorkspace('workspace.picker.subject', { count: moving.length, name: moving[0]?.name ?? '' })}
          </div>

          {/*
            경로는 **한 줄로만** 그린다 (2026-08-18 2차).
            그전에는 줄바꿈을 허용했는데, 폴더 이름이 길거나 깊이 들어가면 두세 줄로 부풀어
            목록이 밀리고 화살표만 남은 줄이 생겼다("이동할 위치 선택" 이 깨져 보이던 것).
            대신 두 가지로 접는다 — ① 3단계 이상이면 가운데를 `…` 로 접고 **끝의 둘만** 보여
            준다(지금 어디인지와 한 단계 위만 알면 된다). ② 마지막 칸은 남는 폭만큼 줄어든다.
          */}
          <nav className="ws-picker-crumb" aria-label={tWorkspace('workspace.aria.breadcrumb')}>
            <button type="button" className="ws-picker-crumb-home" onClick={() => setPath([])}>
              <Home size={13} aria-hidden /> {tWorkspace('workspace.title')}
            </button>
            {path.length > CRUMB_TAIL && (
              <span className="ws-picker-crumb-step">
                <ChevronRight size={12} aria-hidden />
                {/* 접힌 가운데 — 누르면 그 바로 위 단계로 올라간다(막다른 표시가 되지 않게). */}
                <button
                  type="button"
                  className="ws-picker-crumb-ellipsis"
                  title={path.slice(0, -CRUMB_TAIL).map((p) => p.name).join(' / ')}
                  onClick={() => setPath(path.slice(0, path.length - CRUMB_TAIL))}
                >
                  …
                </button>
              </span>
            )}
            {path.slice(-CRUMB_TAIL).map((p) => {
              const depth = path.findIndex((x) => x.id === p.id) + 1;
              return (
                <span key={p.id} className="ws-picker-crumb-step">
                  <ChevronRight size={12} aria-hidden />
                  <button type="button" onClick={() => setPath(path.slice(0, depth))}>
                    {/* 이름이 길어도 경로가 모달 밖으로 밀리지 않게 — 잘라서 보여 준다. */}
                    <span className="ws-picker-crumb-name" title={p.name}>{p.name}</span>
                  </button>
                </span>
              );
            })}
          </nav>

          <ul className="ws-picker-list">
            {loading && <li className="ws-picker-empty">{tWorkspace('workspace.grid.loading')}</li>}
            {!loading && folders.length === 0 && (
              <li className="ws-picker-empty">{tWorkspace('workspace.picker.no_subfolder')}</li>
            )}
            {folders.map((f) => {
              // 자기 자신(또는 이동 대상)에 들어가면 순환이 된다 — 서버도 막지만 먼저 막는다.
              const blocked = movingIds.has(f.id);
              return (
                <li key={f.id}>
                  <button
                    type="button"
                    className="ws-picker-item"
                    disabled={blocked || busy}
                    title={blocked ? tWorkspace('workspace.picker.blocked_self') : undefined}
                    onClick={() => setPath([...path, f])}
                  >
                    <Folder size={16} aria-hidden style={f.color ? { color: f.color } : undefined} />
                    <span>{f.name}</span>
                    <ChevronRight size={13} aria-hidden className="ws-picker-arrow" />
                  </button>
                </li>
              );
            })}
          </ul>

          {error && <div className="workspace-modal-error" role="alert">{error}</div>}

          <footer className="workspace-modal-footer">
            <Button variant="secondary"
              onClick={onClose} disabled={busy}
            >
              {tWorkspace('workspace.modal.btn_cancel')}
            </Button>
            <Button variant="primary"
              onClick={() => void confirm()} disabled={busy}
            >
              {/*
                2026-08-18 — 그전에는 '{폴더이름} 에 넣기' 였다. 이름이 길면 버튼이 그만큼
                길어지는데 모달은 400px 고정 + overflow:hidden 이라 취소 버튼이 밖으로 밀려
                잘렸다. 어디에 넣는지는 바로 위 경로가 이미 말하고 있다.
              */}
              {busy
                ? tWorkspace('workspace.picker.btn_busy')
                : tWorkspace(mode === 'move' ? 'workspace.picker.btn_move' : 'workspace.picker.btn_copy')}
            </Button>
          </footer>
        </div>
      </div>
    </div>
  );
}
